import { useMemo } from 'react';
import type { Matcher } from 'react-day-picker';
import { Calendar } from '@/components/ui/calendar';
import type { GuideDiaryType } from '@/hooks/useGuideAvailability';
import { cn } from '@/lib/utils';
import { parseCalendarDate, toCalendarDate } from '@/util/util_format';

interface GuideAvailabilityCalendarProps {
  /**
   * The guide's diary, or null when there is none to draw — the calendar then
   * renders as a greyed-out, inert sample of itself. See below for why the grid
   * is shown at all in that state.
   */
  diary: GuideDiaryType | null;
  /** Inclusive `yyyy-mm-dd` bounds of the fetched window. */
  fromDate: string;
  toDate: string;
  /** `yyyy-mm-dd`, or '' for nothing chosen yet. */
  value: string;
  /** Omitted with a null diary, where there is nothing to pick. */
  onChange?: (date: string) => void;
}

/**
 * A month of one guide's diary: the days they are free, and the days they are not.
 *
 * The state of a day is drawn rather than merely enforced, which is the whole
 * point of showing a calendar at all — a concierge with a guest at the desk
 * needs to see which week is worth suggesting, not discover a day is taken by
 * clicking it. Booked days are disabled *and* marked, for the reason given in
 * `index.css`: disabling alone renders them like last Tuesday.
 *
 * Days beyond the fetched window are disabled too. We genuinely do not know
 * about them, and an undecorated day inside a month that is otherwise marked up
 * would read as "free" — the one thing it does not mean.
 *
 * With a null diary the same grid is drawn greyed out and inert: no day is
 * marked either way and none can be picked. It is there as a picture of what is
 * coming, next to the notice that says so — an empty panel would leave the
 * promise of a calendar unillustrated, and a live-looking one would invite
 * clicks that go nowhere.
 */
export function GuideAvailabilityCalendar({
  diary,
  fromDate,
  toDate,
  value,
  onChange,
}: GuideAvailabilityCalendarProps) {
  const earliest = parseCalendarDate(fromDate);
  const latest = parseCalendarDate(toDate);
  const selected = parseCalendarDate(value);

  // /portal sends the free days; every other day in the window is taken. Marking
  // the taken ones needs them enumerated, so the window is walked rather than
  // the diary — a day nobody mentioned is exactly the case being drawn.
  const modifiers = useMemo(() => {
    const available: Date[] = [];
    const booked: Date[] = [];
    if (!diary) return { available, booked };

    for (let date = fromDate; date <= toDate; date = nextCalendarDate(date)) {
      const parsed = parseCalendarDate(date);
      if (!parsed) continue;
      if (diary.has(date)) available.push(parsed);
      else booked.push(parsed);
    }

    return { available, booked };
  }, [diary, fromDate, toDate]);

  // Everything we were not told about, plus the days we were told are gone.
  // With no diary that is every day there is.
  const disabled: Matcher[] = diary
    ? [
        ...(earliest ? [{ before: earliest }] : []),
        ...(latest ? [{ after: latest }] : []),
        modifiers.booked,
      ]
    : [() => true];

  return (
    <div>
      {/* Last line of defence. The grid fits its panel at every breakpoint, but a
          zoomed browser or a wider font can eat the margin — and a calendar that
          scrolls beats one that pushes the dialog off the screen. Only effective
          because the columns carry `min-w-0`; see the profile dialog.

          Hidden from screen readers when there is no diary: a month of disabled
          days is nothing to read out, and the notice beside it already says what
          to do instead. */}
      <div
        aria-hidden={!diary}
        className={cn('tw:overflow-x-auto', !diary && 'tw:pointer-events-none tw:opacity-50 tw:select-none')}
      >
        <Calendar
          className='ed-calendar-large'
          mode='single'
          selected={selected}
          defaultMonth={selected ?? earliest}
          startMonth={earliest}
          endMonth={latest}
          disabled={disabled}
          modifiers={modifiers}
          modifiersClassNames={{
            available: 'ed-day-available',
            booked: 'ed-day-booked',
          }}
          onSelect={(date) => onChange?.(date ? toCalendarDate(date) : '')}
        />
      </div>

      {/* Nothing is coloured without a diary, so a key to the colours would be
          explaining marks that are not on the page. */}
      {diary && (
        <div className='tw:mt-4 tw:flex tw:flex-wrap tw:gap-5 tw:text-xs'>
          <LegendKey className='tw:border-ed-green tw:bg-ed-green/10' label='Available' />
          <LegendKey className='tw:border-ed-red tw:bg-ed-red/5' label='Not available' />
        </div>
      )}
    </div>
  );
}

function nextCalendarDate(date: string): string {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

function LegendKey({ className, label }: { className: string; label: string }) {
  return (
    <span className='tw:flex tw:items-center tw:gap-2'>
      <span className={`tw:size-3 tw:border-l-2 ${className}`} />
      <span className='tw:text-ed-stone'>{label}</span>
    </span>
  );
}
